import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["track", "thumb", "label", "panel", "input", "chevron", "dimlevelSwitch", "dimlevelCustom"]

  connect() {
    this.current = null

    // 1. Find ALL panels managed by this controller instance
    // Note: This assumes the controller is attached to a parent wrapping BOTH panels
    // If the controller is on the FORM, panelTargets will find both #dim-custom-panel and #dim-schedule-panel

    if (this.hasPanelTarget) {
      // We need to loop through all panel targets to attach listeners
      // Since static targets return a single element, we use the element query
      const panels = this.element.querySelectorAll('[data-dimlevel-target="panel"]')

      panels.forEach(panel => {
        const showHandler = () => this._handleCollapse(panel, true)
        const hideHandler = () => this._handleCollapse(panel, false)

        panel.addEventListener("show.bs.collapse", showHandler)
        panel.addEventListener("hide.bs.collapse", hideHandler)

        // Store handlers to remove them later if needed (optional but good practice)
        panel._dimlevelShowHandler = showHandler
        panel._dimlevelHideHandler = hideHandler
      })
    }

    if (document.readyState === "complete") {
      this.activate({ params: { val: "" } })
    } else {
      window.addEventListener("load", () => this.activate({ params: { val: "" } }), { once: true })
    }
  }

  disconnect() {
    // Clean up listeners to prevent memory leaks
    if (this.hasPanelTarget) {
      const panels = this.element.querySelectorAll('[data-dimlevel-target="panel"]')
      panels.forEach(panel => {
        if (panel._dimlevelShowHandler) {
          panel.removeEventListener("show.bs.collapse", panel._dimlevelShowHandler)
          panel.removeEventListener("hide.bs.collapse", panel._dimlevelHideHandler)
        }
      })
    }
  }

  activate({ params: { val } }) {
    const v = String(val ?? "")
    this.current = v
    this.labelTargets.forEach(l =>
      l.classList.toggle("active", l.dataset.dimlevelValParam === v)
    )
    this._moveThumb(v)
    this._syncHidden()
    if (this.hasDimlevelSwitchTarget) this.dimlevelSwitchTarget.value = v
  }

  syncFromInput() {
    this._syncHidden()
  }

  _syncHidden() {
    if (!this.hasDimlevelCustomTarget) return
    this.dimlevelCustomTarget.value = this.hasInputTarget ? this.inputTarget.value : ""
  }

  _moveThumb(v) {
    const lbl = this.labelTargets.find(l => l.dataset.dimlevelValParam === v)
    if (!lbl) return
    const trackRect = this.trackTarget.getBoundingClientRect()
    const lblRect   = lbl.getBoundingClientRect()
    this.thumbTarget.style.left  = `${lblRect.left - trackRect.left}px`
    this.thumbTarget.style.width = `${lblRect.width}px`
  }

  // New method to handle the rotation logic
  _handleCollapse(panel, isOpen) {
    const panelId = panel.id
    if (!panelId) return

    // Find the toggle link: <a href="#panelId">
    const toggleLink = document.querySelector(`a[href="#${panelId}"]`)
    if (!toggleLink) return

    // Find the chevron INSIDE that specific link
    const chevron = toggleLink.querySelector('[data-dimlevel-target="chevron"]')
    if (!chevron) return

    // Apply rotation
    chevron.style.display = "inline-block"
    chevron.style.transition = "transform 0.2s ease"
    chevron.style.transform = isOpen ? "rotate(90deg)" : "rotate(0deg)"
  }
}
